import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  apiLogin,
  apiRegister,
  apiLogout,
  apiGetMe,
  apiGetProviders,
  setAuthToken,
} from '../../services/api';

/**
 * Auth slice — Redux Toolkit
 *
 * Holds the current customer's authentication state. Persisted to localStorage
 * via redux-persist so refreshes don't bounce the user back to the sign-in
 * page. The httpOnly cr_token cookie set by the backend is the primary
 * credential — this slice mirrors it in memory so UI components can react.
 */

const initialState = {
  user: null,                 // { _id, name, email, role, provider, avatar, ... }
  token: null,                // mirror of the cr_token cookie value (for Bearer header)
  status: 'idle',             // idle | loading | succeeded | failed
  error: null,
  providers: { local: true, google: false }, // populated on app boot
};

// ── Thunks ──────────────────────────────────────────────────────────────────

export const loginUser = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const res = await apiLogin({ email, password });
      return res.data; // { token, user }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Sign-in failed');
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/register',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await apiRegister(payload);
      return res.data; // { token, user }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Sign-up failed');
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_arg, { rejectWithValue }) => {
    try {
      await apiLogout();
      return true;
    } catch (err) {
      // Even if the server call fails, we want to clear local auth state.
      return rejectWithValue(err.response?.data?.message || 'Logout failed');
    }
  }
);

// Called on app boot to refresh user data using the persisted token / cookie.
// Silent no-op if no token is present.
export const fetchCurrentUser = createAsyncThunk(
  'auth/fetchMe',
  async (_arg, { rejectWithValue, getState }) => {
    const { auth } = getState();
    if (!auth.token) return null;
    try {
      const res = await apiGetMe();
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Session expired');
    }
  }
);

export const fetchAuthProviders = createAsyncThunk(
  'auth/fetchProviders',
  async (_arg, { rejectWithValue }) => {
    try {
      const res = await apiGetProviders();
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Unable to load providers');
    }
  }
);

// ── Slice ───────────────────────────────────────────────────────────────────

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * Manual setter — used by the Google OAuth callback page after the
     * backend redirects us back with the JWT in the URL fragment.
     */
    setCredentials(state, action) {
      const { token, user } = action.payload;
      state.token = token || state.token;
      if (user) state.user = user;
      state.status = 'succeeded';
      state.error = null;
      setAuthToken(state.token);
    },
    clearAuth(state) {
      state.user = null;
      state.token = null;
      state.status = 'idle';
      state.error = null;
      setAuthToken(null);
    },
  },
  extraReducers: (builder) => {
    builder
      // ── login ─────
      .addCase(loginUser.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = action.payload.user;
        state.token = action.payload.token;
        setAuthToken(action.payload.token);
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })
      // ── register ─────
      .addCase(registerUser.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = action.payload.user;
        state.token = action.payload.token;
        setAuthToken(action.payload.token);
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })
      // ── logout ─────
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.status = 'idle';
        setAuthToken(null);
      })
      .addCase(logoutUser.rejected, (state) => {
        // Force-clear client side even if server logout failed
        state.user = null;
        state.token = null;
        state.status = 'idle';
        setAuthToken(null);
      })
      // ── /me boot ─────
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        if (action.payload) state.user = action.payload;
      })
      .addCase(fetchCurrentUser.rejected, (state) => {
        // Token is invalid/expired — clear it
        state.user = null;
        state.token = null;
        setAuthToken(null);
      })
      // ── providers ─────
      .addCase(fetchAuthProviders.fulfilled, (state, action) => {
        state.providers = { ...state.providers, ...action.payload };
      });
  },
});

export const { setCredentials, clearAuth } = authSlice.actions;

// ── Selectors ───────────────────────────────────────────────────────────────
export const selectAuth          = (s) => s.auth;
export const selectCurrentUser   = (s) => s.auth.user;
export const selectIsAuthenticated = (s) => Boolean(s.auth.user && s.auth.token);
export const selectAuthStatus    = (s) => s.auth.status;
export const selectAuthError     = (s) => s.auth.error;
export const selectAuthProviders = (s) => s.auth.providers;

export default authSlice.reducer;
